/**
 * PipelinePhaseManager - Componente para gerenciar fases de um pipeline
 * 
 * Permite adicionar, editar, excluir e reordenar as fases
 * de um pipeline de vendas com drag and drop.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  GripVertical,
  Edit2,
  Trash2,
  Check,
  X,
  Trophy,
  XCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';

// Logger
const logger = createLogger('PipelinePhaseManager');

// Interface da fase
interface PipelinePhase {
  id: string;
  name: string;
  color: string | null;
  icon: string | null;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
  probability_percent: number | null;
  auto_move_days: number | null;
}

// Props do componente
interface PipelinePhaseManagerProps {
  pipelineId: string;
  tenantId: string;
  onClose: () => void;
}

// Cores predefinidas para seleção
const PRESET_COLORS = [
  '#94A3B8', // Slate
  '#60A5FA', // Blue
  '#34D399', // Emerald
  '#FBBF24', // Amber
  '#F97316', // Orange
  '#EF4444', // Red
  '#A855F7', // Purple
  '#EC4899', // Pink
  '#22C55E', // Green
  '#06B6D4', // Cyan
];

// Componente de item arrastável
function SortablePhaseItem({
  phase,
  onEdit,
  onDelete,
  isEditing,
  editData,
  onEditChange,
  onSave,
  onCancel
}: {
  phase: PipelinePhase;
  onEdit: (phase: PipelinePhase) => void;
  onDelete: (phase: PipelinePhase) => void;
  isEditing: boolean;
  editData: Partial<PipelinePhase>;
  onEditChange: (data: Partial<PipelinePhase>) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('crm');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isEditing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border-2 border-primary"
      >
        <div className="flex items-center gap-2 flex-1">
          <Input
            value={editData.name || ''}
            onChange={e => onEditChange({ ...editData, name: e.target.value })}
            placeholder={t('phases.name', 'Nome da fase')}
            className="flex-1 h-8"
            autoFocus
          />
          <Input
            type="color"
            value={editData.color || '#94A3B8'}
            onChange={e => onEditChange({ ...editData, color: e.target.value })}
            className="w-10 h-8 p-0.5 cursor-pointer"
          />
          <Input
            type="number"
            value={editData.probability_percent ?? 0}
            onChange={e => onEditChange({ ...editData, probability_percent: parseInt(e.target.value) || 0 })}
            placeholder="%"
            className="w-16 h-8"
            min={0}
            max={100}
          />
          <div className="flex items-center gap-1">
            <Trophy className={cn("h-4 w-4", editData.is_won ? "text-green-500" : "text-muted-foreground/30")} />
            <Switch
              checked={editData.is_won || false}
              onCheckedChange={v => onEditChange({ ...editData, is_won: v, is_lost: v ? false : editData.is_lost })}
              className="data-[state=checked]:bg-green-500"
            />
          </div>
          <div className="flex items-center gap-1">
            <XCircle className={cn("h-4 w-4", editData.is_lost ? "text-red-500" : "text-muted-foreground/30")} />
            <Switch
              checked={editData.is_lost || false}
              onCheckedChange={v => onEditChange({ ...editData, is_lost: v, is_won: v ? false : editData.is_won })}
              className="data-[state=checked]:bg-red-500"
            />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onSave}>
            <Check className="h-4 w-4 text-green-600" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}>
            <X className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-3 bg-card rounded-lg border hover:border-primary/50 transition-colors group"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      
      <div 
        className="w-3 h-3 rounded-full shrink-0" 
        style={{ backgroundColor: phase.color || '#94A3B8' }}
      />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{phase.name}</span>
          {phase.is_won && (
            <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30">
              <Trophy className="h-3 w-3 mr-1" />
              {t('phases.won', 'Ganho')}
            </Badge>
          )}
          {phase.is_lost && (
            <Badge variant="outline" className="text-xs bg-red-500/10 text-red-600 border-red-500/30">
              <XCircle className="h-3 w-3 mr-1" />
              {t('phases.lost', 'Perdido')}
            </Badge>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {phase.probability_percent !== null && (
          <Badge variant="secondary" className="text-xs">
            {phase.probability_percent}%
          </Badge>
        )}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7"
            onClick={() => onEdit(phase)}
          >
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => onDelete(phase)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function PipelinePhaseManager({ pipelineId, tenantId, onClose }: PipelinePhaseManagerProps) {
  const { t } = useTranslation('crm');
  const queryClient = useQueryClient();
  
  // Estados
  const [phases, setPhases] = useState<PipelinePhase[]>([]);
  const [editingPhaseId, setEditingPhaseId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<PipelinePhase>>({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newPhaseData, setNewPhaseData] = useState<Partial<PipelinePhase>>({
    name: '',
    color: '#94A3B8',
    probability_percent: 50,
    is_won: false,
    is_lost: false
  });
  const [deletePhase, setDeletePhase] = useState<PipelinePhase | null>(null);

  // Sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Query para buscar fases
  const { data: phasesData, isLoading } = useQuery({
    queryKey: ['crm-pipeline-phases', pipelineId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('*')
        .eq('pipeline_id', pipelineId)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as PipelinePhase[];
    },
    enabled: !!pipelineId
  });

  // Atualiza estado local quando dados chegam
  useEffect(() => {
    if (phasesData) {
      setPhases(phasesData);
    }
  }, [phasesData]);

  // Mutation para criar fase
  const createMutation = useMutation({
    mutationFn: async (data: Partial<PipelinePhase>) => {
      const maxSortOrder = Math.max(...phases.map(p => p.sort_order), -1);
      
      const { data: result, error } = await supabase
        .from('crm_pipeline_phases')
        .insert({
          pipeline_id: pipelineId,
          tenant_id: tenantId,
          name: data.name,
          color: data.color,
          probability_percent: data.probability_percent,
          is_won: data.is_won || false,
          is_lost: data.is_lost || false,
          sort_order: maxSortOrder + 1
        })
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: (newPhase) => {
      setPhases(prev => [...prev, newPhase]);
      setIsAddingNew(false);
      setNewPhaseData({
        name: '',
        color: '#94A3B8',
        probability_percent: 50,
        is_won: false,
        is_lost: false
      });
      // Invalida cache para refletir no modal de workspaces
      queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
      toast.success(t('phases.created', 'Fase criada com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao criar fase', error);
      toast.error(error.message || t('phases.createError', 'Erro ao criar fase'));
    }
  });

  // Mutation para atualizar fase
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<PipelinePhase> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('crm_pipeline_phases')
        .update({
          name: data.name,
          color: data.color,
          probability_percent: data.probability_percent,
          is_won: data.is_won,
          is_lost: data.is_lost
        })
        .eq('id', data.id)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: (updatedPhase) => {
      setPhases(prev => prev.map(p => p.id === updatedPhase.id ? updatedPhase : p));
      setEditingPhaseId(null);
      setEditData({});
      // Invalida cache para refletir no modal de workspaces
      queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
      toast.success(t('phases.updated', 'Fase atualizada com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao atualizar fase', error);
      toast.error(error.message || t('phases.updateError', 'Erro ao atualizar fase'));
    }
  });

  // Mutation para deletar fase
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Verificação preventiva: impede exclusão se existem deals vinculados
      const { count, error: countError } = await supabase
        .from('crm_deals')
        .select('id', { count: 'exact', head: true })
        .eq('phase_id', id);

      if (countError) {
        logger.error('Erro ao verificar deals vinculados à fase:', countError.message);
        throw new Error('Erro ao verificar negócios vinculados. Tente novamente.');
      }

      if (count && count > 0) {
        throw new Error(
          `Não é possível remover esta fase. Existem ${count} negócio(s) vinculado(s). ` +
          `Mova os negócios para outra fase antes de excluir.`
        );
      }

      const { error } = await supabase
        .from('crm_pipeline_phases')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      if (deletePhase) {
        setPhases(prev => prev.filter(p => p.id !== deletePhase.id));
      }
      setDeletePhase(null);
      // Invalida cache para refletir no modal de workspaces
      queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
      toast.success(t('phases.deleted', 'Fase removida com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao deletar fase', error);
      // Mensagem amigável para erros de FK ou validação
      const msg = error.message?.includes('foreign key') || error.message?.includes('negócio')
        ? error.message
        : 'Erro ao remover fase. Verifique se existem negócios vinculados.';
      toast.error(msg);
    }
  });

  // Mutation para reordenar fases
  const reorderMutation = useMutation({
    mutationFn: async (orderedPhases: PipelinePhase[]) => {
      const updates = orderedPhases.map((phase, index) => ({
        id: phase.id,
        sort_order: index
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('crm_pipeline_phases')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      // Invalida cache para refletir no modal de workspaces
      queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
    },
    onError: (error: any) => {
      logger.error('Erro ao reordenar fases', error);
      toast.error(t('phases.reorderError', 'Erro ao reordenar fases'));
      // Reverter para dados originais
      if (phasesData) {
        setPhases(phasesData);
      }
    }
  });

  // Handler de drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = phases.findIndex(p => p.id === active.id);
      const newIndex = phases.findIndex(p => p.id === over.id);
      
      const newPhases = arrayMove(phases, oldIndex, newIndex);
      setPhases(newPhases);
      reorderMutation.mutate(newPhases);
    }
  };

  // Handlers
  const handleEdit = (phase: PipelinePhase) => {
    setEditingPhaseId(phase.id);
    setEditData({
      name: phase.name,
      color: phase.color,
      probability_percent: phase.probability_percent,
      is_won: phase.is_won,
      is_lost: phase.is_lost
    });
  };

  const handleSaveEdit = () => {
    if (!editingPhaseId || !editData.name?.trim()) {
      toast.error(t('phases.nameRequired', 'Nome é obrigatório'));
      return;
    }
    updateMutation.mutate({ id: editingPhaseId, ...editData });
  };

  const handleCancelEdit = () => {
    setEditingPhaseId(null);
    setEditData({});
  };

  const handleAddNew = () => {
    if (!newPhaseData.name?.trim()) {
      toast.error(t('phases.nameRequired', 'Nome é obrigatório'));
      return;
    }
    createMutation.mutate(newPhaseData);
  };

  const handleConfirmDelete = () => {
    if (deletePhase) {
      deleteMutation.mutate(deletePhase.id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3 py-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      {/* Lista de Fases com Drag and Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={phases.map(p => p.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {phases.map(phase => (
              <SortablePhaseItem
                key={phase.id}
                phase={phase}
                onEdit={handleEdit}
                onDelete={setDeletePhase}
                isEditing={editingPhaseId === phase.id}
                editData={editData}
                onEditChange={setEditData}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Formulário para adicionar nova fase */}
      {isAddingNew ? (
        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border-2 border-dashed border-primary">
          <div className="flex items-center gap-2 flex-1">
            <Input
              value={newPhaseData.name || ''}
              onChange={e => setNewPhaseData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t('phases.name', 'Nome da fase')}
              className="flex-1 h-8"
              autoFocus
            />
            <Input
              type="color"
              value={newPhaseData.color || '#94A3B8'}
              onChange={e => setNewPhaseData(prev => ({ ...prev, color: e.target.value }))}
              className="w-10 h-8 p-0.5 cursor-pointer"
            />
            <Input
              type="number"
              value={newPhaseData.probability_percent ?? 50}
              onChange={e => setNewPhaseData(prev => ({ ...prev, probability_percent: parseInt(e.target.value) || 0 }))}
              placeholder="%"
              className="w-16 h-8"
              min={0}
              max={100}
            />
            <div className="flex items-center gap-1" title={t('phases.wonTooltip', 'Marcar como fase de ganho')}>
              <Trophy className={cn("h-4 w-4", newPhaseData.is_won ? "text-green-500" : "text-muted-foreground/30")} />
              <Switch
                checked={newPhaseData.is_won || false}
                onCheckedChange={v => setNewPhaseData(prev => ({ ...prev, is_won: v, is_lost: v ? false : prev.is_lost }))}
                className="data-[state=checked]:bg-green-500"
              />
            </div>
            <div className="flex items-center gap-1" title={t('phases.lostTooltip', 'Marcar como fase de perda')}>
              <XCircle className={cn("h-4 w-4", newPhaseData.is_lost ? "text-red-500" : "text-muted-foreground/30")} />
              <Switch
                checked={newPhaseData.is_lost || false}
                onCheckedChange={v => setNewPhaseData(prev => ({ ...prev, is_lost: v, is_won: v ? false : prev.is_won }))}
                className="data-[state=checked]:bg-red-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={handleAddNew}
              disabled={createMutation.isPending}
            >
              <Check className="h-4 w-4 text-green-600" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={() => setIsAddingNew(false)}
            >
              <X className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        </div>
      ) : (
        <Button 
          variant="outline" 
          className="w-full border-dashed"
          onClick={() => setIsAddingNew(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('phases.addNew', 'Adicionar Fase')}
        </Button>
      )}

      <Separator />

      {/* Legenda */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Trophy className="h-3.5 w-3.5 text-green-500" />
          <span>{t('phases.wonLegend', 'Fase de ganho')}</span>
        </div>
        <div className="flex items-center gap-1">
          <XCircle className="h-3.5 w-3.5 text-red-500" />
          <span>{t('phases.lostLegend', 'Fase de perda')}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">%</span>
          <span>{t('phases.probabilityLegend', 'Probabilidade de conversão')}</span>
        </div>
      </div>

      {/* Botão de Fechar */}
      <div className="flex justify-end pt-2">
        <Button onClick={onClose}>
          {t('common.close', 'Fechar')}
        </Button>
      </div>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={!!deletePhase} onOpenChange={() => setDeletePhase(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('phases.deleteTitle', 'Excluir Fase')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('phases.deleteDescription', 'Tem certeza que deseja excluir a fase "{name}"? Os negócios nesta fase serão movidos para a fase anterior.', { name: deletePhase?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t('common.deleting', 'Excluindo...') : t('common.delete', 'Excluir')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
